import { useState, useEffect } from "react";
import axios from "axios";
import {
    HiOutlineMail,
    HiOutlineClock,
    HiOutlineUser,
    HiOutlineClipboardCopy,
    HiCheck,
    HiOutlineSearch,
    HiChevronLeft,
    HiChevronRight,
} from "react-icons/hi";

const ITEMS_PER_PAGE = 10;

export default function MailHistory({ baseUrl, formatDate }) {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Track which email just got copied
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            setError("");

            try {
                const resp = await axios.get(`${baseUrl}/email-history`);
                setEmails(resp.data.data || []);
            } catch (err) {
                console.error(err);
                setError("Failed to load message history");
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [baseUrl]);

    // Filtering
    const filteredEmails = emails.filter((email) => {
        const matchesSearch =
            email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.from_name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDate = filterDate
            ? new Date(email.sent_at).toISOString().slice(0, 10) === filterDate
            : true;

        return matchesSearch && matchesDate;
    });

    // Pagination
    const totalPages = Math.ceil(filteredEmails.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedEmails = filteredEmails.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterDate]);

    const copyHtml = async (email) => {
        try {
            let converted = email.html
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/\\n/g, "\n");

            converted = converted.replace(/<\/?[^>]+(>|$)/g, "");

            await navigator.clipboard.writeText(converted);
            setCopiedId(email.id);

            setTimeout(() => setCopiedId(null), 4000);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };

    if (loading) {
        return (
            <div className="py-10 text-center text-sm text-gray-400">
                Loading history…
            </div>
        );
    }
    if (error) {
        return <div className="py-6 text-center text-sm text-red-600">{error}</div>;
    }

    return (
        <div>
            {/* Search & Date Filter */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                    <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search by subject or sender"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    />
                </div>
                <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 sm:w-44"
                />
            </div>

            {filteredEmails.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No emails found.</p>
            ) : (
                <>
                    <div className="space-y-2.5">
                        {paginatedEmails.map((email) => (
                            <details
                                key={email.id}
                                className="group border border-gray-200 rounded-lg bg-white open:shadow-sm"
                            >
                                <summary className="list-none cursor-pointer px-3 py-2.5 flex items-start gap-2.5">
                                    <HiOutlineMail className="text-indigo-500 w-4 h-4 mt-0.5 shrink-0" />

                                    <div className="min-w-0 flex-1">
                                        <p className="text-[13.5px] font-medium text-gray-800 truncate">
                                            {email.subject}
                                        </p>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <HiOutlineUser className="w-3.5 h-3.5" />
                                                {email.from_name}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <HiOutlineClock className="w-3.5 h-3.5" />
                                                {formatDate(email.sent_at)}
                                            </span>
                                            <span>
                                                Sent to{" "}
                                                <span className="font-semibold text-gray-700">
                                                    {email.total_sent}
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            copyHtml(email);
                                        }}
                                        className="shrink-0 flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-md hover:bg-indigo-50 transition-colors"
                                    >
                                        {copiedId === email.id ? (
                                            <>
                                                <HiCheck className="text-green-600 w-3.5 h-3.5" /> Copied
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineClipboardCopy className="w-3.5 h-3.5" /> Copy
                                            </>
                                        )}
                                    </button>
                                </summary>

                                <div
                                    className="border-t border-gray-100 px-3 py-3 text-[13px] prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: email.html }}
                                />
                            </details>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-end items-center gap-1.5 mt-3">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`flex items-center gap-0.5 px-2.5 py-1 text-[12.5px] rounded-md border transition-colors
                                ${currentPage === 1
                                    ? "text-gray-300 border-gray-200"
                                    : "text-indigo-600 border-gray-200 hover:bg-indigo-50"}`}
                        >
                            <HiChevronLeft className="w-3.5 h-3.5" /> Prev
                        </button>

                        <span className="text-[12.5px] text-gray-500 px-1">
                            {currentPage} / {totalPages}
                        </span>

                        <button
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`flex items-center gap-0.5 px-2.5 py-1 text-[12.5px] rounded-md border transition-colors
                                ${currentPage === totalPages
                                    ? "text-gray-300 border-gray-200"
                                    : "text-indigo-600 border-gray-200 hover:bg-indigo-50"}`}
                        >
                            Next <HiChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}